import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Camera,
  ChevronDown,
  Heart,
  Menu,
  Search,
  ShoppingBag,
  User,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { AnnouncementBar } from './AnnouncementBar';
import { Container } from './Primitives';
import { useCart } from '../../hooks/useCart';
import { navItems as nav } from '../../data/megamenuNav';
import { useAppSelector } from '../../store/hooks';
import { selectAuthReady } from '../../store/actions/authActions';

const megaFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Shell = styled.header`
  position: sticky;
  top: 0;
  z-index: 40;
  background: ${({ theme }) => theme.colors.noir};
  color: ${({ theme }) => theme.colors.cream};
`;

const UtilityRow = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.noirSoft};
`;

const UtilityInner = styled(Container)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 3.5rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    height: 4rem;
  }
`;

const UtilGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.$gap ?? '1.25rem'};
`;

const MenuToggle = styled.button`
  padding: 0.25rem;
  margin-left: -0.25rem;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const Logo = styled(Link)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  user-select: none;
`;

const LogoText = styled.span`
  margin: 0;
  display: block;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.375rem, 2vw, 1.875rem);
  letter-spacing: 0.22em;
  line-height: 1;
  color: ${({ theme }) => theme.colors.cream};
  font-weight: 400;
`;

const LogoAccent = styled.span`
  color: ${({ theme }) => theme.colors.champagneLight};
`;

const IconBtn = styled.button`
  position: relative;
  padding: 0.5rem;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors.champagneLight};
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  background: ${({ theme }) => theme.colors.terracotta};
  color: ${({ theme }) => theme.colors.cream};
  font-size: 10px;
  font-weight: 600;
  border-radius: 999px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

const CatNav = styled.nav`
  display: none;
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.noirSoft};
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
  }
`;

const CatNavInner = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.75rem 2rem;
  min-height: 3rem;
`;

const CatNavItem = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

const CatLink = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: calc(${({ theme }) => theme.type.navCategory} - 0.04rem);
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.09em;
  text-transform: uppercase;
  font-weight: 500;
  padding: 0.35rem 0;
  color: rgba(245, 242, 235, 0.8);
  border-bottom: 1px solid transparent;
  transition: color 0.2s ease;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: calc(${({ theme }) => theme.type.navCategoryLg} - 0.05rem);
    letter-spacing: 0.11em;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.champagneLight};
  }

  &.is-active {
    color: ${({ theme }) => theme.colors.cream};
    border-bottom-color: ${({ theme }) => theme.colors.champagneLight};
  }

  &.accent {
    color: ${({ theme }) => theme.colors.terracottaLight};
    &:hover {
      color: ${({ theme }) => theme.colors.terracotta};
    }
  }

  &.accent.is-active {
    color: ${({ theme }) => theme.colors.terracottaLight};
    border-bottom-color: transparent;
  }
`;

const MegaPanel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  background: ${({ theme }) => theme.colors.cream};
  color: ${({ theme }) => theme.colors.noir};
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 24px 40px -20px rgba(0, 0, 0, 0.25);
  animation: ${megaFade} 0.2s ease both;
  z-index: 50;
`;

const MegaInner = styled(Container)`
  padding-top: clamp(2rem, 3vw, 2.85rem);
  padding-bottom: clamp(2rem, 3vw, 2.85rem);
`;

const MegaRow = styled.div`
  display: grid;
  gap: clamp(1.5rem, 3vw, 3rem);
  align-items: start;
  grid-template-columns: ${(p) =>
    p.$hasSpotlight
      ? 'minmax(0, 1fr) minmax(380px, 0.58fr)'
      : 'minmax(0, 1fr)'};
`;

const MegaColumns = styled.div`
  min-width: 0;
`;

const MegaSpotlightAside = styled.aside`
  min-width: 0;
  width: 100%;
  justify-self: end;
  align-self: start;
  max-width: min(100%, 620px);

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    max-width: min(100%, 700px);
  }
`;

const MegaGrid = styled.div`
  display: grid;
  gap: clamp(1.25rem, 2.2vw, 2.5rem);
  grid-template-columns: ${(p) => `repeat(${p.$cols}, minmax(0, 1fr))`};
`;

const MegaColHeadingLink = styled(Link)`
  display: inline-block;
  margin-bottom: clamp(0.75rem, 1.2vw, 1rem);
  font-size: calc(${({ theme }) => theme.type.megaColHeading} - 0.03rem);
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 500;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.noir};
  border-bottom: 1px solid ${({ theme }) => theme.colors.noir};
  padding-bottom: 0.35rem;
  &:hover {
    color: ${({ theme }) => theme.colors.terracotta};
  }
`;

const MegaColHeading = styled.h4`
  margin: 0 0 clamp(0.75rem, 1.2vw, 1rem);
  display: inline-block;
  font-size: calc(${({ theme }) => theme.type.megaColHeading} - 0.03rem);
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 500;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.noir};
  border-bottom: 1px solid ${({ theme }) => theme.colors.noir};
  padding-bottom: 0.35rem;
`;

const MegaLinkList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(0.35rem, 0.55vw, 0.55rem);
`;

const MegaLink = styled(Link)`
  font-size: calc(${({ theme }) => theme.type.megaLink} - 0.03rem);
  letter-spacing: 0.045em;
  line-height: 1.35;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.68);
  &:hover {
    color: ${({ theme }) => theme.colors.noir};
  }
`;

const MegaSpotlightCard = styled(Link)`
  display: block;
  color: inherit;
`;

const MegaSpotlightImgWrap = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  max-height: clamp(300px, 44vh, 520px);
  width: 100%;
  overflow: hidden;
  
`;

const MegaSpotlightImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  transition: transform 0.55s ${({ theme }) => theme.easeLuxe};
  ${MegaSpotlightCard}:hover & {
    transform: scale(1.05);
  }
`;

const SearchPanel = styled.div`
  overflow: hidden;
  transition: max-height 0.3s ease;
  max-height: ${(p) => (p.$open ? '5.5rem' : '0')};
  border-bottom: ${(p) =>
    p.$open ? `1px solid ${p.theme.colors.noirSoft}` : 'none'};
`;

const SearchRow = styled(Container)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-top: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(245, 242, 235, 0.3);
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: ${({ theme }) => theme.type.searchInput};
  color: ${({ theme }) => theme.colors.cream};
  &::placeholder {
    color: rgba(245, 242, 235, 0.5);
  }
`;

const MobilePanel = styled.div`
  display: block;
  overflow: hidden;
  transition: max-height 0.45s ease;
  max-height: ${(p) => (p.$open ? '85vh' : '0')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.noirSoft};
  background: ${({ theme }) => theme.colors.noir};
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const MobileNavInner = styled(Container)`
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const MobileRow = styled.div`
  border-bottom: 1px solid rgba(245, 242, 235, 0.15);
`;

const MobileRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MobileExpandBtn = styled.button`
  padding: 0.75rem;
  background: transparent;
  border: none;
  color: rgba(245, 242, 235, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileNavLink = styled(NavLink)`
  flex: 1;
  padding: 0.75rem 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.35rem, 5vw, 2rem);
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.cream};

  &.accent {
    color: ${({ theme }) => theme.colors.terracottaLight};
  }

  &.is-active {
    color: ${({ theme }) => theme.colors.champagneLight};
  }
`;

const MobileMegaBlock = styled.div`
  padding-bottom: 1rem;
  padding-left: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MobileSpotlightWrap = styled(Link)`
  display: block;
  margin-top: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(245, 242, 235, 0.15);
`;

const MobileSpotlightImgWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: clamp(260px, 45vh, 420px);
  overflow: hidden;
  background: hsl(0 0% 18%);
  margin-bottom: 0.65rem;
`;

const MobileSpotlightImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const MobileMegaHeading = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.champagneLight};
`;

const MobileMegaLink = styled(Link)`
  font-size: ${({ theme }) => theme.type.megaLink};
  letter-spacing: 0.04em;
  line-height: 1.4;
  color: rgba(245, 242, 235, 0.88);
  display: block;
  padding: 0.125rem 0;
`;

const MobileMegaList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const DesktopOnly = styled.span`
  display: none;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 500;
  color: inherit;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.champagneDark};
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: inline-flex;
  }
`;

const HideSm = styled(IconBtn)`
  @media (max-width: 639px) {
    display: none;
  }
`;

const IconHeaderLink = styled(Link)`
  position: relative;
  padding: 0.5rem;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: ${({ theme }) => theme.colors.champagneLight};
  }
  @media (max-width: 639px) {
    display: none;
  }
`;

const catLinkClassFn = (n, megaOpen) => ({ isActive }) => {
  const bits = [];
  if (n.accent) bits.push('accent');
  if (isActive || (megaOpen && !n.accent)) bits.push('is-active');
  return bits.join(' ');
};

export function Header() {
  const { t } = useTranslation();
  const loggedIn = useAppSelector(selectAuthReady);
  const { count, open: openCart } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setOpenMega(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  return (
    <Shell>
      <AnnouncementBar />
      <UtilityRow>
        <UtilityInner>
          <UtilGroup>
            <MenuToggle
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t('modamai.header.toggleMenu')}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </MenuToggle>
            <DesktopOnly as="a" href="#" onClick={(e) => e.preventDefault()}>
              <Camera size={16} /> 248K
            </DesktopOnly>
          </UtilGroup>

          <Logo to="/">
            <LogoText>
              MODA <LogoAccent>MAI</LogoAccent>
            </LogoText>
          </Logo>

          <UtilGroup $gap="0.15rem">
            <IconBtn
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label={t('modamai.header.search')}
            >
              <Search size={18} />
            </IconBtn>
            <IconHeaderLink
              to={loggedIn ? '/account' : '/login'}
              aria-label={loggedIn ? t('modamai.header.account') : t('modamai.header.signIn')}
            >
              <User size={18} />
            </IconHeaderLink>
            <HideSm type="button" aria-label={t('modamai.header.wishlist')}>
              <Heart size={18} />
            </HideSm>
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <IconBtn type="button" onClick={openCart} aria-label={t('modamai.header.cart')}>
                <ShoppingBag size={18} />
              </IconBtn>
              {count > 0 && <CartBadge>{count}</CartBadge>}
            </div>
          </UtilGroup>
        </UtilityInner>
      </UtilityRow>

      <CatNav onMouseLeave={() => setOpenMega(null)}>
        <CatNavInner>
          {nav.map((n) => {
            const hasMega = !!n.mega;
            const megaOpen = openMega === n.label;
            return (
              <CatNavItem
                key={n.to}
                onMouseEnter={() => setOpenMega(hasMega ? n.label : null)}
              >
                <CatLink
                  to={n.to}
                  className={catLinkClassFn(n, megaOpen)}
                >
                  {n.label}
                  {hasMega && (
                    <ChevronDown
                      size={12}
                      strokeWidth={2}
                      style={{ opacity: 0.6 }}
                      aria-hidden
                    />
                  )}
                </CatLink>
              </CatNavItem>
            );
          })}
        </CatNavInner>

        {nav.map((n) => {
          if (!n.mega || openMega !== n.label) return null;
          const { columns, spotlight } = n.mega;
          const colCount = Math.min(columns.length, 6);
          const hasSpotlight = !!spotlight;
          return (
            <MegaPanel
              key={n.label}
              onMouseEnter={() => setOpenMega(n.label)}
            >
              <MegaInner>
                <MegaRow $hasSpotlight={hasSpotlight}>
                  <MegaColumns>
                    <MegaGrid $cols={colCount}>
                      {columns.map((c) => (
                        <div key={c.heading}>
                          {c.to ? (
                            <MegaColHeadingLink to={c.to}>
                              {c.heading}
                            </MegaColHeadingLink>
                          ) : (
                            <MegaColHeading>{c.heading}</MegaColHeading>
                          )}
                          <MegaLinkList>
                            {c.links.map((l) => (
                              <li key={l.label}>
                                <MegaLink to={l.to}>{l.label}</MegaLink>
                              </li>
                            ))}
                          </MegaLinkList>
                        </div>
                      ))}
                    </MegaGrid>
                  </MegaColumns>
                  {spotlight && (
                    <MegaSpotlightAside>
                      <MegaSpotlightCard to={spotlight.to}>
                        <MegaSpotlightImgWrap>
                          <MegaSpotlightImg
                            src={spotlight.image}
                            alt={spotlight.alt ?? ''}
                            loading="lazy"
                          />
                        </MegaSpotlightImgWrap>
                      </MegaSpotlightCard>
                    </MegaSpotlightAside>
                  )}
                </MegaRow>
              </MegaInner>
            </MegaPanel>
          );
        })}
      </CatNav>

      <SearchPanel $open={searchOpen}>
        <SearchRow
          as="form"
          onSubmit={(e) => {
            e.preventDefault();
            const q = searchQuery.trim();
            if (!q) return;
            setSearchOpen(false);
            navigate(`/search?q=${encodeURIComponent(q)}`);
          }}
        >
          <Search size={16} style={{ opacity: 0.6 }} />
          <SearchInput
            type="search"
            name="q"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus={searchOpen}
            placeholder={t('modamai.header.searchPlaceholder')}
          />
          <IconBtn
            type="button"
            aria-label={t('modamai.header.closeSearch')}
            onClick={() => setSearchOpen(false)}
          >
            <X size={16} />
          </IconBtn>
        </SearchRow>
      </SearchPanel>

      <MobilePanel $open={mobileOpen}>
        <MobileNavInner>
          {nav.map((n) => {
            const hasMega = !!n.mega;
            const expanded = mobileExpanded === n.label;
            return (
              <MobileRow key={n.to}>
                <MobileRowTop>
                  <MobileNavLink
                    to={n.to}
                    className={({ isActive }) =>
                      `${n.accent ? 'accent' : ''} ${isActive ? 'is-active' : ''}`.trim()
                    }
                  >
                    {n.label}
                  </MobileNavLink>
                  {hasMega && (
                    <MobileExpandBtn
                      type="button"
                      aria-expanded={expanded}
                      aria-label={
                        expanded
                          ? t('modamai.header.collapseNav', { label: n.label })
                          : t('modamai.header.expandNav', { label: n.label })
                      }
                      onClick={() =>
                        setMobileExpanded(expanded ? null : n.label)
                      }
                    >
                      <ChevronDown
                        size={20}
                        style={{
                          transform: expanded ? 'rotate(180deg)' : undefined,
                          transition: 'transform 0.2s ease',
                        }}
                      />
                    </MobileExpandBtn>
                  )}
                </MobileRowTop>
                {hasMega && expanded && (
                  <MobileMegaBlock>
                    {n.mega.columns.map((col) => (
                      <div key={col.heading}>
                        <MobileMegaHeading>{col.heading}</MobileMegaHeading>
                        <MobileMegaList>
                          {col.links.map((l) => (
                            <li key={l.label}>
                              <MobileMegaLink to={l.to}>{l.label}</MobileMegaLink>
                            </li>
                          ))}
                        </MobileMegaList>
                      </div>
                    ))}
                    {n.mega.spotlight && (
                      <MobileSpotlightWrap to={n.mega.spotlight.to}>
                        <MobileSpotlightImgWrap>
                          <MobileSpotlightImg
                            src={n.mega.spotlight.image}
                            alt={n.mega.spotlight.alt ?? ''}
                            loading="lazy"
                          />
                        </MobileSpotlightImgWrap>
                      </MobileSpotlightWrap>
                    )}
                  </MobileMegaBlock>
                )}
              </MobileRow>
            );
          })}
          <div
            style={{
              marginTop: '1.5rem',
              display: 'flex',
              gap: '1.5rem',
              fontSize: '11px',
              fontFamily: 'inherit',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              opacity: 0.6,
            }}
          >
            <Link to={loggedIn ? '/account' : '/login'} style={{ color: 'inherit', textDecoration: 'none' }}>
              {loggedIn ? t('modamai.header.account') : t('modamai.header.signIn')}
            </Link>
            <span>{t('modamai.header.wishlist')}</span>
          </div>
        </MobileNavInner>
      </MobilePanel>
    </Shell>
  );
}
